import { ConfigDataType, CustomClaims } from '../types';

/**
 * Resolves the route keys a user may see from `config/routesPermissions`.
 *
 * Extracted because `App.tsx` (which builds the `<Route>` elements) and `MainLinks`
 * (which builds the sidebar) need exactly the same answer — when the two copies of this
 * logic drifted, a user could see a nav item that rendered nothing, or reach a URL with
 * no way back to it.
 *
 * Access is entirely config driven: to expose a route to a claim, add its key to that
 * claim's array in `config/routesPermissions`. Nothing is granted implicitly here.
 *
 * Note this governs *visibility* only. Acting inside a route can need more — the product
 * catalogue's letter and announcement tools additionally require the `fund_manager`
 * claim plus assignment on the fund, enforced in the callables and the security rules.
 */
export const resolveAvailableRouteKeys = (
    routePermissions: ConfigDataType['routesPermissions'],
    customClaims: CustomClaims,
): string[] => {
    if (!routePermissions) return [];

    // The declared type of the config value is stale (it predates most route keys), so the
    // entries are read as plain string arrays — which is what the documents actually hold.
    const permissions = routePermissions as unknown as Record<string, string[]>;

    return [...new Set(
        Object.keys(permissions)
            .filter((permission) => Object.keys(customClaims).includes(permission))
            .flatMap((permission) => permissions[permission] || []),
    )];
};
